<template>
    <div class="email-setting-preferences">
        <h3 class="email-setting-preferences__title typographies-heading-h3">
            {{ translations.preferencesTitle }}
        </h3>
        <div class="email-setting-preferences__container">
            <div class="email-setting-preferences__content">
                <div>
                    <p class="typographies-paragraph-medium-semibold">
                        {{ translations.copyEmails.title }}
                    </p>
                    <p
                        class="email-setting-preferences__description typographies-paragraph-medium-regular"
                    >
                        {{ translations.copyEmails.description }}
                    </p>
                </div>
                <CompassSwitch
                    :value="copyEmailValue"
                    :disabled="disableCopyEmailSwitch"
                    @click="handleCopyEmailsSwitch"
                />
            </div>
            <div class="email-setting-preferences__content">
                <div>
                    <p class="typographies-paragraph-medium-semibold">
                        {{ translations.automaticallyAttachDocument.title }}
                    </p>
                    <p
                        class="email-setting-preferences__description typographies-paragraph-medium-regular"
                    >
                        {{ translations.automaticallyAttachDocument.description }}
                    </p>
                </div>
                <CompassSwitch
                    :value="automaticallyAttachDocumentValue"
                    :disabled="disableAutomaticallyAttachDocumentSwitch"
                    @click="handleAutomaticallyAttachDocumentSwitch"
                />
            </div>
            <div v-if="privateEmailPermissions" class="email-setting-preferences__content">
                <div>
                    <p class="typographies-paragraph-medium-semibold">
                        {{ translations.privateEmails.title }}
                    </p>
                    <p
                        class="email-setting-preferences__description typographies-paragraph-medium-regular"
                    >
                        {{ translations.privateEmails.description }}
                    </p>
                </div>
                <CompassSwitch
                    :value="privateEmailValue"
                    :disabled="disablePrivateEmailSwitch"
                    @click="handlePrivateEmailSwitch"
                />
            </div>
        </div>
    </div>
</template>

<script>
import StaffApi from 'api/staff/staff';
import { setEmailSettings } from 'api/emailEngine/emailEngineService';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    props: {
        defaultValues: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            translations: {
                preferencesTitle: this.$t.__('email.EmailSettingPreferences.title'),
                privateEmails: {
                    title: this.$t.__('email.EmailSettingPreferences.privateEmails.title'),
                    description: this.$t.__(
                        'email.EmailSettingPreferences.privateEmails.description',
                    ),
                },
                copyEmails: {
                    title: this.$t.__('email.EmailSettingPreferences.copyEmails.title'),
                    description: this.$t.__('email.EmailSettingPreferences.copyEmails.description'),
                },
                automaticallyAttachDocument: {
                    title: this.$t.__(
                        'email.EmailSettingPreferences.automaticallyAttachDocument.title',
                    ),
                    description: this.$t.__(
                        'email.EmailSettingPreferences.automaticallyAttachDocument.description',
                    ),
                },
            },
            privateEmailPermissions: false,
            privateEmailValue: this.defaultValues.privateEmail,
            disablePrivateEmailSwitch: false,
            staffId: null,
            copyEmailValue: this.defaultValues.copyEmail,
            disableCopyEmailSwitch: false,
            automaticallyAttachDocumentValue: this.defaultValues.automaticallyAttachDocumentValue,
            disableAutomaticallyAttachDocumentSwitch: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        getLabelPrivateEmailSwitchToaster() {
            return this.privateEmailValue
                ? this.$t.__('email.EmailSettingPreferences.privateEmails.isPrivate')
                : this.$t.__('email.EmailSettingPreferences.privateEmails.isNotPrivate');
        },
        getLabelCopyEmailSwitchToaster() {
            return this.copyEmailValue
                ? this.$t.__('email.EmailSettingPreferences.copyEmails.isCopied')
                : this.$t.__('email.EmailSettingPreferences.copyEmails.isNotCopied');
        },
    },
    watch: {
        defaultValues(newValue) {
            this.privateEmailValue = newValue.privateEmail;
            this.copyEmailValue = newValue.copyEmail;
            this.automaticallyAttachDocumentValue = newValue.automaticallyAttachDocument;
        },
    },
    created() {
        this.staffId = this.$store.getters['user/getUser'].id;
    },
    mounted() {
        this.getPrivateEmailPermission(this.staffId);
    },
    methods: {
        getPrivateEmailPermission(staffId) {
            StaffApi.getStaffPermission(staffId)
                .then((response) => {
                    this.privateEmailPermissions =
                        response?.data?._embed?.acl?.update_email_privacy;
                })
                .catch((error) => {
                    this.errorsHandler.logException(error);
                });
        },
        handlePrivateEmailSwitch() {
            if (this.disablePrivateEmailSwitch) {
                return;
            }
            this.disablePrivateEmailSwitch = true;
            this.privateEmailValue = !this.privateEmailValue;
            const params = {
                private_mode: this.privateEmailValue,
            };
            setEmailSettings(params)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        close: true,
                        text: this.getLabelPrivateEmailSwitchToaster,
                    });
                })
                .catch((error) => {
                    this.privateEmailValue = !this.privateEmailValue;
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        close: true,
                        text: this.$t.__('email.EmailSettingPreferences.privateEmails.error'),
                    });
                })
                .finally(() => {
                    this.disablePrivateEmailSwitch = false;
                });
        },
        handleCopyEmailsSwitch() {
            if (this.disableCopyEmailSwitch) {
                return;
            }
            this.disableCopyEmailSwitch = true;
            this.copyEmailValue = !this.copyEmailValue;
            const params = {
                add_sender_to_email: this.copyEmailValue,
            };
            setEmailSettings(params)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        close: true,
                        text: this.getLabelCopyEmailSwitchToaster,
                    });
                })
                .catch((error) => {
                    this.copyEmailValue = !this.copyEmailValue;
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        close: true,
                        text: this.$t.__('email.EmailSettingPreferences.copyEmails.error'),
                    });
                })
                .finally(() => {
                    this.disableCopyEmailSwitch = false;
                });
        },
        handleAutomaticallyAttachDocumentSwitch() {
            if (this.disableAutomaticallyAttachDocumentSwitch) {
                return;
            }
            this.disableAutomaticallyAttachDocumentSwitch = true;
            this.automaticallyAttachDocumentValue = !this.automaticallyAttachDocumentValue;
            const params = {
                automatically_attach_document: this.automaticallyAttachDocumentValue,
            };
            setEmailSettings(params)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        close: true,
                        text: this.$t.__(
                            'email.EmailSettingPreferences.automaticallyAttachDocument.success',
                        ),
                    });
                })
                .catch((error) => {
                    this.automaticallyAttachDocumentValue = !this.automaticallyAttachDocumentValue;
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        close: true,
                        text: this.$t.__(
                            'email.EmailSettingPreferences.automaticallyAttachDocument.error',
                        ),
                    });
                })
                .finally(() => {
                    this.disableAutomaticallyAttachDocumentSwitch = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.email-setting-preferences {
    margin: var(spacing-5) 0 0;

    p {
        margin: 0;
    }

    &__title {
        margin: 0 0 var(spacing-1);
    }

    &__container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0);
        padding: var(--spacing-3);
    }
    &__content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing-5);
    }
    &__description {
        color: var(--colors-common-text-subtle-default);
    }
}
</style>
